import {
	type FileId,
	FileType,
	type SuretyBondFormTemplateId,
	type SuretyBondFormType,
} from "@inrev/common";
import { useFileUpload } from "@portal/api/bond-request/agent";
import { useMemo, useState } from "react";
import { v4 } from "uuid";
import { EMPTY_BOND_FORM } from "../../../../constants";
import type { BondFormData, UploadFile } from "../../../../types";
import { getNameAndExtensionFromFileName } from "../../../../utils";

export type BondFormUploadFile = UploadFile<typeof FileType.blank_bond_form>;

type UseBondFormUploadArgs = {
	value: BondFormData;
	onChange: (value: BondFormData) => void;
	onBlur?: () => void;
	typeLabels: Partial<Record<SuretyBondFormType, string>>;
};

export const useBondFormUpload = ({
	value,
	onChange,
	onBlur,
	typeLabels,
}: UseBondFormUploadArgs) => {
	// `null` means the type-select modal is closed; an array means it is open for those files.
	const [pendingTypeSelection, setPendingTypeSelection] = useState<File[] | null>(null);
	const [filesPendingUpload, setFilesPendingUpload] = useState<BondFormUploadFile[]>([]);
	const [fileIdsPendingDelete, setFileIdsPendingDelete] = useState<string[]>([]);
	// Bridges the type selected in the modal until the parent commits it back through `value`.
	const [selectedUploadType, setSelectedUploadType] = useState<SuretyBondFormType | "">("");

	const allowedTypes = useMemo(() => Object.keys(typeLabels) as SuretyBondFormType[], [typeLabels]);

	const uploadType = value.upload.type || (filesPendingUpload.length > 0 ? selectedUploadType : "");
	const filesUploaded = value.upload.files.length > 0 || filesPendingUpload.length > 0;
	const isOnlyFile = value.upload.files.length + filesPendingUpload.length === 1;

	const commit = (next: BondFormData) => {
		onChange(next);
		onBlur?.();
	};

	const clearPending = (args: { fileUploads: { pendingFile: BondFormUploadFile }[] }) =>
		setFilesPendingUpload((pending) =>
			pending.filter(
				(file) => !args.fileUploads.some((upload) => upload.pendingFile.id === file.id),
			),
		);

	const { uploadFn, deleteFn } = useFileUpload<typeof FileType.blank_bond_form>({
		onUploadSuccess: (data, args) => {
			clearPending(args);
			commit({
				templateId: "",
				upload: { type: uploadType, files: [...value.upload.files, ...data] },
			});
		},
		onUploadError: (error, args) => {
			console.error(error);
			if (isOnlyFile) {
				setFilesPendingUpload([]);
				commit(EMPTY_BOND_FORM);
			} else {
				clearPending(args);
			}
		},
		onDeleteSuccess: (args) => {
			commit(
				isOnlyFile
					? EMPTY_BOND_FORM
					: {
							templateId: "",
							upload: {
								...value.upload,
								files: value.upload.files.filter((file) => file.id !== args.id),
							},
						},
			);
			setFileIdsPendingDelete((ids) => ids.filter((fileId) => fileId !== args.id));
		},
		onDeleteError: (error, args) => {
			console.error(error);
			setFileIdsPendingDelete((ids) => ids.filter((fileId) => fileId !== args.id));
		},
	});

	const startUpload = (rawFiles: File[], type: SuretyBondFormType | "") => {
		const fileUploads = rawFiles.map((rawFile) => {
			const { fileName, extension } = getNameAndExtensionFromFileName(rawFile.name);
			return {
				rawFile,
				pendingFile: {
					id: v4() as FileId,
					name: fileName,
					extension,
					types: [FileType.blank_bond_form],
				},
			};
		});
		setSelectedUploadType(type);
		setFilesPendingUpload((pending) => [
			...pending,
			...fileUploads.map((upload) => upload.pendingFile),
		]);
		uploadFn({ fileUploads });
	};

	const handleDrop = (rawFiles: File[]) => {
		if (uploadType !== "") {
			startUpload(rawFiles, uploadType);
			return;
		}
		// Hosts that do not classify bond forms pass no types, leaving nothing to prompt for.
		if (allowedTypes.length <= 1) {
			startUpload(rawFiles, allowedTypes[0] ?? "");
			return;
		}
		setPendingTypeSelection(rawFiles);
	};

	const handleTypeSelected = (types: SuretyBondFormType[]) => {
		const type = types[0];
		const rawFiles = pendingTypeSelection ?? [];
		setPendingTypeSelection(null);
		if (!type || rawFiles.length === 0) return;
		startUpload(rawFiles, type);
	};

	const handleDelete = (fileId: string) => {
		setFileIdsPendingDelete((ids) => [...ids, fileId]);
		deleteFn({ id: fileId });
	};

	return {
		allowedTypes,
		uploadType,
		filesUploaded,
		filesPendingUpload,
		fileIdsPendingDelete,
		pendingTypeSelection,
		handleDrop,
		handleDelete,
		handleTypeSelected,
		cancelTypeSelection: () => setPendingTypeSelection(null),
		selectTemplate: (templateId: SuretyBondFormTemplateId) =>
			commit({ templateId, upload: EMPTY_BOND_FORM.upload }),
		deselectTemplate: () => commit(EMPTY_BOND_FORM),
	};
};
